import React, { useMemo, useState } from "react";
import { splitQuotedHtml, splitQuotedPlainText } from "../../util/email-quoted-content";

interface SanitizedMarkUp {
    __html: string;
}

interface HtmlBodyProps {
    kind: "html";
    html: string;
    sanitize: (html: string) => SanitizedMarkUp;
}

interface PlainBodyProps {
    kind: "plain";
    plain: string;
}

type Props = HtmlBodyProps | PlainBodyProps;

const TOGGLE_BUTTON_CLASSES =
    "inline-flex items-center justify-center px-2 py-0.5 my-1 text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded text-xs leading-none";

export const EmailBodyWithQuoteToggle: React.FC<Props> = props => {
    const [expanded, setExpanded] = useState(false);

    const split = useMemo(() => {
        if (props.kind === "html") {
            return splitQuotedHtml(props.html);
        }
        return splitQuotedPlainText(props.plain);
    }, [props]);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded(prev => !prev);
    };

    if (props.kind === "html") {
        const primaryMarkup = props.sanitize(split.primary);
        const quotedMarkup = split.hasQuoted ? props.sanitize(split.quoted) : null;

        return (
            <>
                <div
                    className="unreset message-body text-sm text-gray-700"
                    dangerouslySetInnerHTML={primaryMarkup}
                />
                {split.hasQuoted ? (
                    <div className="mt-1">
                        <button
                            type="button"
                            className={TOGGLE_BUTTON_CLASSES}
                            onClick={handleToggle}
                            title={expanded ? "Hide quoted text" : "Show quoted text"}>
                            <span aria-hidden="true">···</span>
                            <span className="sr-only">
                                {expanded ? "Hide quoted text" : "Show quoted text"}
                            </span>
                        </button>
                        {expanded && quotedMarkup ? (
                            <div
                                className="unreset message-body text-sm text-gray-500 mt-1 border-l-2 border-gray-200 pl-3"
                                dangerouslySetInnerHTML={quotedMarkup}
                            />
                        ) : null}
                    </div>
                ) : null}
            </>
        );
    }

    return (
        <>
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
                {split.primary}
            </div>
            {split.hasQuoted ? (
                <div className="mt-1">
                    <button
                        type="button"
                        className={TOGGLE_BUTTON_CLASSES}
                        onClick={handleToggle}
                        title={expanded ? "Hide quoted text" : "Show quoted text"}>
                        <span aria-hidden="true">···</span>
                        <span className="sr-only">
                            {expanded ? "Hide quoted text" : "Show quoted text"}
                        </span>
                    </button>
                    {expanded ? (
                        <div className="text-sm text-gray-500 whitespace-pre-wrap mt-1 border-l-2 border-gray-200 pl-3">
                            {split.quoted}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};
